import { FC } from 'react';
import {
    Form,
    Input,
    Modal,
    Select,
    Switch,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { useCoverageContext } from './context/coverage.ctx';
import { ICoverage } from '../../../interfaces/coverage.interface';

const CARD_GROUP = ['CARD', 'BILLING_CARD', 'BILLING_TRANSFER'];
const VISIBLE_PAYMENT_METHODS = ['CASH', 'CARD', 'TERMINAL'];
const VEHICLE_TYPES = ['LITE', 'BIKE'];

const toVisibleMethods = (methods: string[]): string[] => {
    const visible: string[] = [];
    if (methods.includes('CASH')) visible.push('CASH');
    if (methods.some((m) => CARD_GROUP.includes(m))) visible.push('CARD');
    if (methods.includes('TERMINAL')) visible.push('TERMINAL');
    return visible;
};

const toApiMethods = (visible: string[]): string[] => {
    const api: string[] = [];
    if (visible.includes('CASH')) api.push('CASH');
    if (visible.includes('CARD')) api.push(...CARD_GROUP);
    if (visible.includes('TERMINAL')) api.push('TERMINAL');
    return api;
};

interface CoverageFormProps {
    coverage?: ICoverage | null;
    onClose: () => void;
}

const CoverageForm: FC<CoverageFormProps> = ({ coverage, onClose }) => {
    const { t } = useTranslation('settings', { keyPrefix: 'SETTINGS.COVERAGE' });
    const [form] = Form.useForm();
    const { handleUpdate, confirmCreate } = useCoverageContext();

    const isEditMode = !!coverage;
    const isVisible = Form.useWatch('isVisible', form);

    const onFinish = (values: any) => {
        const payload = {
            name: values.name,
            paymentMethod: toApiMethods(values.paymentMethod),
            carName: values.carName,
            isVisible: values.isVisible,
        };

        if (isEditMode) {
            handleUpdate(coverage._id, payload);
        } else {
            confirmCreate(payload);
        }
        onClose();
    };

    return (
        <Modal
            title={isEditMode ? t('EDIT_COVERAGE') : t('NEW_COVERAGE')}
            open
            onCancel={onClose}
            onOk={() => form.submit()}
            okText={t('SAVE')}
            cancelText={t('CANCEL')}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={isEditMode ? {
                    name: coverage.name || '',
                    paymentMethod: toVisibleMethods(coverage.paymentMethod || []),
                    carName: coverage.carName || [],
                    isVisible: coverage.isVisible,
                } : {
                    name: '',
                    paymentMethod: ['CASH'],
                    carName: ['LITE'],
                    isVisible: true,
                }}
                onFinish={onFinish}
            >
                <Form.Item
                    name="name"
                    label={t('NAME')}
                    rules={[{ required: true, message: t('NAME_REQUIRED') }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="paymentMethod"
                    label={t('PAYMENT_METHODS')}
                >
                    <Select
                        mode="multiple"
                        options={VISIBLE_PAYMENT_METHODS.map((pm) => ({ label: t(`PAYMENT_METHOD_${pm}`), value: pm }))}
                    />
                </Form.Item>

                <Form.Item
                    name="carName"
                    label={t('VEHICLE_TYPES')}
                >
                    <Select
                        mode="multiple"
                        options={VEHICLE_TYPES.map((vt) => ({ label: t(`VEHICLE_TYPE_${vt}`), value: vt }))}
                    />
                </Form.Item>

                <Form.Item
                    name="isVisible"
                    label={isVisible ? t('ACTIVE') : t('INACTIVE')}
                    extra={t('VISIBILITY_INFO')}
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export { CoverageForm };
